import { useState, useCallback } from 'react';
import { supabase } from '@/lib/api/supabase';
import type {
  WaitlistEntry,
  WaitlistSpotNotification,
  WaitlistStatus,
  WaitlistNotificationType,
} from '@/types';

export interface UseWaitlistState {
  loading: boolean;
  error: string | null;
}

export interface UseWaitlistReturn extends UseWaitlistState {
  // User-facing operations
  joinWaitlist: (tournamentId: string) => Promise<{ success: boolean; position?: number; error?: string }>;
  leaveWaitlist: (tournamentId: string) => Promise<{ success: boolean; error?: string }>;
  getWaitlistPosition: (tournamentId: string) => Promise<number | null>;
  getUserWaitlistEntry: (tournamentId: string) => Promise<WaitlistEntry | null>;
  getUserWaitlistEntries: () => Promise<WaitlistEntry[]>;

  // Admin operations
  getTournamentWaitlist: (tournamentId: string) => Promise<WaitlistEntry[]>;
  promoteFromWaitlist: (waitlistId: string) => Promise<{ success: boolean; error?: string }>;
  removeFromWaitlist: (waitlistId: string) => Promise<{ success: boolean; error?: string }>;

  // Admin notifications
  getUnprocessedNotifications: () => Promise<WaitlistSpotNotification[]>;
  markNotificationProcessed: (notificationId: string) => Promise<{ success: boolean; error?: string }>;

  // Stats
  getWaitlistCount: (tournamentId: string) => Promise<number>;
  getTotalWaitlistCount: () => Promise<number>;
}

/**
 * Convert database row to WaitlistEntry
 */
function dbToWaitlistEntry(db: any): WaitlistEntry {
  return {
    id: db.id,
    userId: db.user_id,
    tournamentId: db.tournament_id,
    position: db.position,
    joinedAt: db.joined_at,
    email: db.email,
    firstName: db.first_name,
    lastName: db.last_name,
    status: db.status as WaitlistStatus,
    promotedAt: db.promoted_at,
    invoiceSentAt: db.invoice_sent_at,
    confirmedAt: db.confirmed_at,
    createdAt: db.created_at,
    updatedAt: db.updated_at,
  };
}

/**
 * Convert database row to WaitlistSpotNotification.
 * Note: notification_type, special_event_id, addon_id, variant_name, and item_name
 * columns require migration 20260316000000 to be applied. Pre-migration they will be undefined.
 */
function dbToWaitlistNotification(db: any): WaitlistSpotNotification {
  return {
    id: db.id,
    notificationType: (db.notification_type || 'tournament') as WaitlistNotificationType,
    tournamentId: db.tournament_id,
    tournamentName: db.tournament_name,
    specialEventId: db.special_event_id,
    addonId: db.addon_id,
    variantName: db.variant_name,
    itemName: db.item_name,
    spotsAvailable: db.spots_available,
    waitlistCount: db.waitlist_count,
    processed: db.processed,
    processedAt: db.processed_at,
    processedBy: db.processed_by,
    createdAt: db.created_at,
  };
}

/**
 * Hook for managing tournament waitlist operations
 * Provides both user-facing and admin operations
 */
export function useWaitlist(): UseWaitlistReturn {
  const [state, setState] = useState<UseWaitlistState>({
    loading: false,
    error: null,
  });

  const setLoading = (loading: boolean) => setState(prev => ({ ...prev, loading }));
  const setError = (error: string | null) => setState(prev => ({ ...prev, error }));

  // ============================================================================
  // User Operations
  // ============================================================================

  /**
   * Join the waitlist for a tournament
   */
  const joinWaitlist = useCallback(async (tournamentId: string): Promise<{ success: boolean; position?: number; error?: string }> => {
    setLoading(true);
    setError(null);

    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        return { success: false, error: 'You must be logged in to join the waitlist' };
      }

      // Business rule: only users with an existing completed event entry may
      // join tournament waitlists. Day passes count (attendance-only holders
      // may wait-list); limit(1) instead of maybeSingle() because a user may
      // hold several day-pass rows.
      const { data: eventRegs, error: eventRegError } = await supabase
        .from('event_registrations')
        .select('id')
        .eq('user_id', user.id)
        .eq('event_year', 2026)
        .eq('payment_status', 'completed')
        .limit(1);

      if (eventRegError) {
        return { success: false, error: 'Could not verify your event registration' };
      }
      if (!eventRegs || eventRegs.length === 0) {
        return {
          success: false,
          error: 'You need an event registration before joining a tournament waitlist.',
        };
      }

      // Get user profile for cached info
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        return { success: false, error: 'Could not find your profile' };
      }

      // Insert waitlist entry (position is auto-assigned by trigger)
      const { data, error } = await supabase
        .from('tournament_waitlist')
        .insert({
          user_id: user.id,
          tournament_id: tournamentId,
          email: user.email,
          first_name: profile.first_name,
          last_name: profile.last_name,
        })
        .select('position')
        .single();

      if (error) {
        if (error.code === '23505') { // Unique violation
          return { success: false, error: 'You are already on the waitlist for this tournament' };
        }
        if (error.message?.includes('already registered')) {
          return { success: false, error: 'You are already registered for this tournament' };
        }
        return { success: false, error: error.message };
      }

      return { success: true, position: data.position };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Leave the waitlist for a tournament
   */
  const leaveWaitlist = useCallback(async (tournamentId: string): Promise<{ success: boolean; error?: string }> => {
    setLoading(true);
    setError(null);

    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        return { success: false, error: 'You must be logged in' };
      }

      const { error } = await supabase
        .from('tournament_waitlist')
        .delete()
        .eq('user_id', user.id)
        .eq('tournament_id', tournamentId)
        .eq('status', 'waiting');

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get current user's position in the waitlist for a tournament
   */
  const getWaitlistPosition = useCallback(async (tournamentId: string): Promise<number | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('tournament_waitlist')
        .select('position')
        .eq('user_id', user.id)
        .eq('tournament_id', tournamentId)
        .eq('status', 'waiting')
        .single();

      if (error || !data) return null;
      return data.position;
    } catch {
      return null;
    }
  }, []);

  /**
   * Get current user's waitlist entry for a tournament
   */
  const getUserWaitlistEntry = useCallback(async (tournamentId: string): Promise<WaitlistEntry | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('tournament_waitlist')
        .select('*')
        .eq('user_id', user.id)
        .eq('tournament_id', tournamentId)
        .eq('status', 'waiting')
        .single();

      if (error || !data) return null;
      return dbToWaitlistEntry(data);
    } catch {
      return null;
    }
  }, []);

  /**
   * Get all waitlist entries for current user
   */
  const getUserWaitlistEntries = useCallback(async (): Promise<WaitlistEntry[]> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('tournament_waitlist')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'waiting')
        .order('joined_at', { ascending: true });

      if (error || !data) return [];
      return data.map(dbToWaitlistEntry);
    } catch {
      return [];
    }
  }, []);

  // ============================================================================
  // Admin Operations
  // ============================================================================

  /**
   * Get full waitlist for a tournament (admin only)
   * Includes waiting, promoted, and invoiced entries
   */
  const getTournamentWaitlist = useCallback(async (tournamentId: string): Promise<WaitlistEntry[]> => {
    setLoading(true);
    setError(null);

    try {
      // Fetch all active waitlist entries (waiting, promoted, invoiced)
      // Exclude cancelled, expired, and confirmed entries
      const { data, error } = await supabase
        .from('tournament_waitlist')
        .select('*')
        .eq('tournament_id', tournamentId)
        .in('status', ['waiting', 'promoted', 'invoiced'])
        .order('position', { ascending: true });

      if (error) {
        setError(error.message);
        return [];
      }

      return data?.map(dbToWaitlistEntry) || [];
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Promote a user from waitlist (admin only)
   * Note: This just marks them as promoted. Admin should then manually create their registration.
   */
  const promoteFromWaitlist = useCallback(async (waitlistId: string): Promise<{ success: boolean; error?: string }> => {
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase
        .from('tournament_waitlist')
        .update({
          status: 'promoted',
          promoted_at: new Date().toISOString(),
        })
        .eq('id', waitlistId)
        .eq('status', 'waiting');

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Remove a user from waitlist (admin only)
   */
  const removeFromWaitlist = useCallback(async (waitlistId: string): Promise<{ success: boolean; error?: string }> => {
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase
        .from('tournament_waitlist')
        .delete()
        .eq('id', waitlistId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================================================
  // Admin Notifications
  // ============================================================================

  /**
   * Get unprocessed spot notifications (admin only)
   */
  const getUnprocessedNotifications = useCallback(async (): Promise<WaitlistSpotNotification[]> => {
    try {
      const { data, error } = await supabase
        .from('waitlist_spot_notifications')
        .select('*')
        .eq('processed', false)
        .order('created_at', { ascending: false });

      if (error || !data) return [];
      return data.map(dbToWaitlistNotification);
    } catch {
      return [];
    }
  }, []);

  /**
   * Mark a notification as processed (admin only)
   */
  const markNotificationProcessed = useCallback(async (notificationId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('waitlist_spot_notifications')
        .update({
          processed: true,
          processed_at: new Date().toISOString(),
          processed_by: user?.id,
        })
        .eq('id', notificationId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      return { success: false, error: message };
    }
  }, []);

  // ============================================================================
  // Stats
  // ============================================================================

  /**
   * Get waitlist count for a specific tournament
   */
  const getWaitlistCount = useCallback(async (tournamentId: string): Promise<number> => {
    try {
      const { data, error } = await supabase.rpc('get_tournament_waiting_count', {
        p_tournament_id: tournamentId,
      });

      if (error) return 0;
      return (data as number) || 0;
    } catch {
      return 0;
    }
  }, []);

  /**
   * Get total waitlist count across all tournaments
   */
  const getTotalWaitlistCount = useCallback(async (): Promise<number> => {
    try {
      const { count, error } = await supabase
        .from('tournament_waitlist')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'waiting');

      if (error) return 0;
      return count || 0;
    } catch {
      return 0;
    }
  }, []);

  return {
    ...state,
    // User operations
    joinWaitlist,
    leaveWaitlist,
    getWaitlistPosition,
    getUserWaitlistEntry,
    getUserWaitlistEntries,
    // Admin operations
    getTournamentWaitlist,
    promoteFromWaitlist,
    removeFromWaitlist,
    // Admin notifications
    getUnprocessedNotifications,
    markNotificationProcessed,
    // Stats
    getWaitlistCount,
    getTotalWaitlistCount,
  };
}
